import type { CurrentUserResponse, Community, Role } from "@/types/api";
import { EnvironmentService } from "@/utils/environment";

export class UserPermissionsService {
    /**
     * Check if user has a top-level system admin role
     */
    static isSystemAdmin(user: CurrentUserResponse): boolean {
        return user.role === "Admin";
    }

    /**
     * Check if user has elevated role (Operator, Admin, or Owner) in any community
     * Users with these roles have access to the web dashboard
     */
    static hasOperatorOrAdminRole(user: CurrentUserResponse): boolean {
        if (this.isSystemAdmin(user)) return true;

        return (
            user.memberships?.some((m) =>
                ["Operator", "Admin", "Owner"].includes(m.communityRole)
            ) ?? false
        );
    }

    /**
     * Get the primary community for a user (first community where they have elevated access)
     */
    static getPrimaryCommunity(user: CurrentUserResponse): Community | null {
        if (this.isSystemAdmin(user) && user.memberships?.length) {
            return user.memberships[0].community;
        }

        return (
            user.memberships?.find((m) =>
                ["Operator", "Admin", "Owner"].includes(m.communityRole)
            )?.community ?? null
        );
    }

    static getRoles(user: CurrentUserResponse): Role[] {
        const roles: Role[] = [];

        // Add top-level roles
        if (user.role === "Admin") {
            roles.push("admin");
        }

        if (!user.memberships?.length) return roles;

        const roleMapping: Record<string, Role> = {
            Owner: "admin",
            Admin: "admin",
            Operator: "operator",
            Member: "member",
        };

        const communityRoles = user.memberships.map(
            (membership) => roleMapping[membership.communityRole] ?? "member",
        );

        return [...new Set([...roles, ...communityRoles])];
    }

    /**
     * Get user's membership for a specific community by orgName
     */
    static getMembershipByOrgName(user: CurrentUserResponse, orgName: string) {
        return user.memberships?.find(m => m.community.orgName === orgName) ?? null;
    }

    /**
     * Get user's membership for the current community (from subdomain)
     */
    static getCurrentCommunityMembership(user: CurrentUserResponse) {
        const orgName = EnvironmentService.getCommunity();
        if (!orgName) return null;
        return this.getMembershipByOrgName(user, orgName);
    }

    /**
     * Get default venue ID for user's current community
     */
    static getDefaultVenueId(user: CurrentUserResponse): number | null {
        const membership = this.getCurrentCommunityMembership(user);
        const venues = membership?.community?.venues;
        if (!venues?.length) return null;
        const defaultVenue = venues.find(v => v.isDefault) || venues[0];
        return defaultVenue?.id ?? null;
    }

    /**
     * Check if user has access to a specific community (operator level).
     * User must have Operator, Admin, or Owner role in this community.
     * 
     * @param user - Current user object
     * @param communityId - ID of the community to check
     * @returns true if user has operator+ access to this community
     */
    static canAccessCommunity(user: CurrentUserResponse, communityId: number): boolean {
        if (this.isSystemAdmin(user)) return true;

        return (
            user.memberships?.some(
                (m) =>
                    m.community.id === communityId &&
                    ["Operator", "Admin", "Owner"].includes(m.communityRole)
            ) ?? false
        );
    }

    /**
     * Check if user has installer access to a specific community.
     * User must have Admin or Owner role in this community.
     * 
     * @param user - Current user object
     * @param communityId - ID of the community to check
     * @returns true if user has installer access to this community
     */
    static canAccessInstallerForCommunity(user: CurrentUserResponse, communityId: number): boolean {
        if (this.isSystemAdmin(user)) return true;

        return (
            user.memberships?.some(
                (m) =>
                    m.community.id === communityId &&
                    ["Admin", "Owner"].includes(m.communityRole)
            ) ?? false
        );
    }

    /**
     * Get user's role in a specific community
     * 
     * @param user - Current user object
     * @param communityId - ID of the community to check
     * @returns Community role or null if not a member
     */
    static getCommunityRole(user: CurrentUserResponse, communityId: number): string | null {
        if (this.isSystemAdmin(user)) return "Admin";

        const membership = user.memberships?.find(
            (m) => m.community.id === communityId
        );
        return membership?.communityRole ?? null;
    }

    /**
     * Build app URL for a user based on their primary community
     */
    static buildAppUrlForUser(user: CurrentUserResponse): string | null {
        const community = this.getPrimaryCommunity(user);
        return community
            ? EnvironmentService.buildCommunityUrl(community.orgName)
            : null;
    }

    /**
     * Build home URL for a user (app URL with trailing slash)
     */
    static buildHomeUrlForUser(user: CurrentUserResponse): string | null {
        const baseUrl = this.buildAppUrlForUser(user);
        return baseUrl ? `${baseUrl}/` : null;
    }

    /**
     * Check if user should be redirected to a different domain
     */
    static shouldRedirectToDifferentDomain(user: CurrentUserResponse): boolean {
        const appUrl = this.buildAppUrlForUser(user);
        if (!appUrl) return false;

        return EnvironmentService.isDifferentOrigin(appUrl);
    }
}
